import React from 'react';
import PropTypes from 'prop-types';
import { CardTitle, Typography, Menu } from '@one-thd/sui-atomic-components';
import { More } from '@one-thd/sui-icons';
import { BuyingGuide } from '@one-thd/sui-icons';
import { getProjectsHeaderActionMap } from './actions';

const MAX_DISPLAY_COUNT = 99;

const ProjectsHeader = ({
  projectCount, actions, actionsClassName, componentId, hideViewAllSuffix
}) => {

  const safeProjectCount = Number.isFinite(projectCount) ? projectCount : 0;
  const hasProjects = safeProjectCount > 0;
  // A Pro with thousands of projects pushed Create New out of the card at the narrowest
  // breakpoints. Capping the display at two digits keeps the header a fixed width. UX signed
  // off on the truncation. No thousands separator is needed once the value cannot exceed 99.
  const displayCount = safeProjectCount > MAX_DISPLAY_COUNT
    ? `${MAX_DISPLAY_COUNT}+`
    : safeProjectCount;

  const actionMap = getProjectsHeaderActionMap({ hasProjects, componentId, hideViewAllSuffix });

  // Filter out invalid actions and prepare the rendered actions with separators
  const validActions = actions.filter((action) => Object.prototype.hasOwnProperty.call(actionMap, action));
  const cardTitleActionContent = validActions.length > 0 ? (
    <div className={`sui-flex sui-flex-row md:sui-flex-col lg:sui-flex-row sui-gap-2 ${actionsClassName ? ` ${actionsClassName}` : ''}`}>
      {validActions.map((action, index) => (
        <div key={`action-${action}-${index}`} className="sui-flex sui-items-center">
          {actionMap[action]}
          {index < validActions.length - 1 && (
            <div className="sui-ml-2 sui-inline md:sui-hidden lg:sui-inline">|</div>
          )}
        </div>
      ))}
    </div>
  ) : null;

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const anchorRef = React.useRef(null);

  const handleMenuToggle = () => {
    setIsMenuOpen((prevOpen) => !prevOpen);
  };

  const handleMenuClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setIsMenuOpen(false);
  };

  const renderedActions = validActions.length > 0 ? (
    <>
      <div className="sui-block md:sui-hidden lg:sui-block">
        {cardTitleActionContent}
      </div>
      <div className="sui-hidden md:sui-block lg:sui-hidden">
        <button 
          type="button"
          ref={anchorRef}
          onClick={handleMenuToggle}
          className="sui-cursor-pointer hover:sui-bg-strong sui-flex sui-px-2 sui-py-1 sui-rounded-md"
        >
          <More />
        </button>
        <Menu
          open={isMenuOpen}
          anchorEl={anchorRef.current}
          onClose={handleMenuClose}
          placement="bottom-end" 
        >
          <div className="sui-px-3 [&_a]:sui-no-underline">
            {cardTitleActionContent}
          </div>
        </Menu>
      </div>
    </>
  ) : null;

  return (
    <div
      className="sui-pb-2 @sm:sui-pb-4 sui-pt-1"
    >
      <CardTitle
        header={(
          <div className="sui-flex sui-gap-3 sui-items-center">
            <BuyingGuide />
            <div className="sui-flex sui-gap-2 sui-items-center">
              <Typography variant="h4" weight="display">
                Projects
              </Typography>
              {hasProjects && (
                <Typography variant="body-base">
                  ({displayCount})
                </Typography>
              )}
            </div>
          </div>
        )}
        data-test-id="projects-header"
        actionPosition="end"
        action={renderedActions}
      />
    </div>
  );
};

ProjectsHeader.propTypes = {
  projectCount: PropTypes.number,
  actions: PropTypes.arrayOf(PropTypes.oneOf(['learnMore', 'createNew', 'viewAll'])),
  actionsClassName: PropTypes.string,
  componentId: PropTypes.string,
  hideViewAllSuffix: PropTypes.bool,
};

ProjectsHeader.defaultProps = {
  projectCount: 0,
  actions: [],
  actionsClassName: '',
  componentId: '',
  hideViewAllSuffix: false,
};

export default ProjectsHeader;
